import _ from 'lodash';
import { useState, useEffect } from 'react';
import { _capitalize } from 'chart.js/helpers';
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Input, Popconfirm, Select, Table, Typography } from 'antd';

import { ListingSecurityKeysThunk, ListingSecurityValueThunk } from '../../Store/Thunks/ListingSecurityThunk';
import { ApiPutListingSecurityValue } from '../../Services';

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ListingSecurityValueData,
  ...restProps
}) => {
  const options = {
    Low: { value: "Low", style: { color: '#4DC7B5' }, label: "Low" },
    Medium: { value: "Medium", style: { color: '#F5B452' }, label: "Medium" },  
    High: { value: "High", style: { color: '#F27281' }, label: "High" }
  };
 
  return (
    <td {...restProps}>
      {editing ? (<>
     <Form.Item name={[record.key, `${dataIndex}Level`]} style={{ margin: 0 }}>
      <Select>
        {Object.keys(options).map(key => (
          <Select.Option
            key={options[key].value}
            value={options[key].value}
            style={options[key].style}
          >
            {options[key].label}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
    <Form.Item  name={[record.key, dataIndex]} style={{ margin: 0 }}>
      <Input />
    </Form.Item>
        </>
      ) : (
        record ? <> <p>{record[`${dataIndex}Level`]} </p> <p>{record[dataIndex]} </p> </> : children 
      )}
    </td>
  );
};

export default EditableCell;

export const Security = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [isSaveClicked, setIsSaveClicked] = useState(false);
  const [modifiedRows, setModifiedRows] = useState(new Set());
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkEditing, setBulkEditing] = useState(false);

  const rowSelection = {
      selectedRowKeys,
      onChange: (keys) => setSelectedRowKeys(keys),
      getCheckboxProps: (record) => ({
        disabled: editingKey !== '' && !selectedRowKeys.includes(record.key),
        }),
  };
    const bulkEdit = () => {
      const values = {};

      data.forEach(row => {
        if (selectedRowKeys.includes(row.key)) {
          values[row.key] = row; // each row keeps its own values
        }
      });

      form.setFieldsValue(values);
      setBulkEditing(true);
    };

    const bulkSave = async () => {
      try {
  
        const values = await form.validateFields();
        const newData = [...data];

        Object.keys(values).forEach(rowKey => {
          const index = newData.findIndex(item => item.key === Number(rowKey));
          if (index > -1) {
            newData[index] = {
              ...newData[index],
              ...values[rowKey],
            };
            modifiedRows.add(newData[index].id);
          }
        });
        

      ApiPutListingSecurityValue({"securities":newData}).then(() => {
      setLoading(false);
      setModifiedRows(new Set());       
    }).catch(() => {
      setLoading(false);
    });
        setData(newData);
        setBulkEditing(false);
        setSelectedRowKeys([]);
      } catch (e) {
        console.log(e);
      }
    };

  useEffect(() => {
    dispatch(ListingSecurityKeysThunk());
    dispatch(ListingSecurityValueThunk());
  }, []);

   const {
    ListingSecurityKeyData,
    ListingSecurityKeyLoading,
    ListingSecurityValueData,
    ListingSecurityValueLoading,
  } = useSelector((state) => state.ListingSecurityReducer);

  const isEditing = (record) => {
    return bulkEditing || record.key === editingKey;
  };

const edit = (record) => {
  form.setFieldsValue({
    [record.key]: {
      ...record,
    },
  });
  setEditingKey(record.key);
};
  const cancel = () => {
    setEditingKey('');
  };
  const reorderKeys = (keysArray) => {
    const reorderedKeys = [];
    const priorityKeys = ["smartContractPlatformSecurityAudit", "projectSecurity"];  
  
    priorityKeys.forEach((key) => {
      const match = keysArray.find((item) => item.key === key);
      if (match) reorderedKeys.push(match);
    });
    
    keysArray.forEach((item) => {
      if (!priorityKeys.includes(item.key)) {
        reorderedKeys.push(item);
      }
    });
  
    return reorderedKeys;
  };
  const reorderedKeys = reorderKeys(ListingSecurityKeyData);

  const dataSource = () => {
    const arr = [];
    ListingSecurityValueData.map((item, index) => {
      const obj = {};
      ListingSecurityKeyData.map((element) => {
        if (item.listingSecurity) {         
          obj[element.key] = item.listingSecurity[element.key];
          obj[`${element.key}Level`] = item.listingSecurity[`${element.key}Level`];
          obj.id = item.listingSecurity.id;
        } else {       
          obj[element.key] = "";
          obj[`${element.key}Level`] = "";
          obj.id = item.id;
        }
      });
      
      arr.push({
        name: item.assetName,
        ...obj,
        key: index,
      });
    });
   
    setData(arr);
    return arr;
  };

  useEffect(() => {
    if (ListingSecurityKeyData?.length && ListingSecurityValueData.length) {
      dataSource();
    }
  }, [ListingSecurityKeyData, ListingSecurityValueData]);

  const editAssetValue = () => {
    setLoading(true);
    const editData = Array.from(modifiedRows).map((id) => {
      const row = data.find((item) => item.id === id);
      const newItem = {};
      for (const key in row) {
        if (key !== 'key' && key !== 'name') {        
          newItem[key] = row[key];
        }
      }
      return newItem;
    });
    ApiPutListingSecurityValue({"securities":editData}).then(() => {
      setLoading(false);
      setModifiedRows(new Set());       
    }).catch(() => {
      setLoading(false);
    });
  };

const save = async (key) => {
  try {
    const values = await form.validateFields();    
    const rowValues = values[key];

    setData(prev =>
      prev.map(row =>
        row.key === key
          ? {
              ...row,
              ...rowValues,
            }
          : row
      )
    );

    // mark row as modified
    const row = data.find(r => r.key === key);
    if (row) {
      setModifiedRows(prev => new Set(prev).add(row.id));
    }

    setEditingKey('');
    setIsSaveClicked(true);
  } catch (err) {
    console.log(err);
  }
};

  useEffect(()=>{
    if(modifiedRows && isSaveClicked){
      editAssetValue();
    }
    setIsSaveClicked(false)
  },[modifiedRows,isSaveClicked])
  
  const columns = () => {
    return [
      {
        title: "#",
        dataIndex: "index",
        key: "index",
        width: 60,
        render: (text, record, index) => index + 1,
      },
      {
        title: 'Asset',
        dataIndex: 'name',
        fixed: 'left',
        width: 100,

      },
      ...reorderedKeys.map((item) => ({
        title: item.name,
        dataIndex: item.key,
        editable: true, 
      })),
      {
        title: 'Operation',
        dataIndex: 'operation',
        fixed: 'right',
        width: 100,
        render: (_, record) => {
          const editable = isEditing(record);         
          return editable && !bulkEditing ? (
            <span>
              <Typography.Link
                onClick={() => save(record.key)}
                style={{ marginRight: 8 }}
              >
                Save
              </Typography.Link>
              <Popconfirm
                    title="Sure to cancel?"
                    okText="Cancel"
                    cancelText="No"
                    okButtonProps={{ style: { float: 'right' } }}
                    cancelButtonProps={{ style: { float: 'left' } }}
                    onConfirm={cancel}
                      overlayStyle={{ paddingBottom:"20px" }}
                  >
                    <a>Cancel</a>
           </Popconfirm>
            </span>
          ) : !bulkEditing ? (
            <Typography.Link disabled={editingKey !== ''} onClick={() => edit(record)}>
              Edit
            </Typography.Link>
          ):null
        },
      },
    ];
  };

  const mergedColumns = columns().map((col) => {
    
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.inputType,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        ListingSecurityValueData
      }),
    };
  });

  return (
    <div>
      <Form form={form} component={false} >
      <Table
          rowSelection={rowSelection}
          loading={ListingSecurityKeyLoading || ListingSecurityValueLoading || loading}
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          pagination={false}
          bordered
          dataSource={data}
          columns={mergedColumns}
          rowClassName="editable-row"
          scroll={{ y: 500 }}
        />
      </Form>
      <div style={{ marginBottom: 16,marginTop:20 }}>
          <Button
            disabled={!selectedRowKeys.length || editingKey !== ''}
            onClick={bulkEdit}
          >Bulk Edit</Button>
          {bulkEditing && (
            <>
              <Button
                type="primary"
                style={{ marginLeft: 8 }}
                onClick={bulkSave}
              > Bulk Save
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                onClick={() => {
                  setBulkEditing(false);
                  setSelectedRowKeys([]);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </div>

    </div>
  );
};